import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, DollarSign, User, Briefcase, Building, PlusCircle, Percent } from 'lucide-react';
import { Rep } from '../types';
import { FINANCE_COMPANIES, getCompanyConfig } from '../utils/financePlans';
import { formatCurrency, SIT_PAY_AMOUNT } from '../utils/helpers';
import { Job } from '../types';
interface CommissionFormProps {
  reps: Rep[];
  onSubmit: (jobData: any) => void;
  editingJob: Job | null;
  onCancelEdit: () => void;
}
const PROJECT_TYPES = ['Windows', 'Roofs', 'General Contracting', 'Hardscape'];
const FINANCE_COMPANY_NAMES = ['None', ...FINANCE_COMPANIES.map(c => c.name), 'Other'];
export function CommissionForm({
  reps,
  onSubmit,
  editingJob,
  onCancelEdit
}: CommissionFormProps) {
  const [sitDate, setSitDate] = useState(new Date().toISOString().split('T')[0]);
  const [closeDate, setCloseDate] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [setterName, setSetterName] = useState('');
  // Project Details (only required if closed)
  const [projectType, setProjectType] = useState(PROJECT_TYPES[0]);
  const [customProjectType, setCustomProjectType] = useState('');
  const [projectTotal, setProjectTotal] = useState('');
  const [financeCompany, setFinanceCompany] = useState('None');
  const [customFinanceCompany, setCustomFinanceCompany] = useState('');
  const [selectedPlanIndex, setSelectedPlanIndex] = useState<number>(0);
  const [customFinanceRate, setCustomFinanceRate] = useState('');
  const [closerName, setCloserName] = useState('');
  const [depositPaid, setDepositPaid] = useState(false);
  // Populate form when editing
  useEffect(() => {
    if (editingJob) {
      setSitDate(editingJob.sitDate || (editingJob as any).dateOfSale || '');
      setCloseDate(editingJob.closeDate || '');
      setCustomerName(editingJob.customerName || '');
      setSetterName(editingJob.setterName || '');
      setProjectType(PROJECT_TYPES.includes(editingJob.projectType) ? editingJob.projectType : PROJECT_TYPES[0]);
      setProjectTotal(editingJob.projectTotal ? String(editingJob.projectTotal) : '');
      setCloserName(editingJob.closerName || '');
      setDepositPaid(editingJob.depositPaid || false);
      // Finance
      const knownCompanies = FINANCE_COMPANY_NAMES.filter(c => c !== 'Other');
      if (knownCompanies.includes(editingJob.financeCompany)) {
        setFinanceCompany(editingJob.financeCompany);
      } else if (editingJob.financeCompany && editingJob.financeCompany !== 'None') {
        setFinanceCompany('Other');
        setCustomFinanceCompany(editingJob.financeCompany);
      } else {
        setFinanceCompany('None');
      }
      if (editingJob.financeRate) {
        setCustomFinanceRate(String((editingJob.financeRate * 100).toFixed(2)));
      }
    }
  }, [editingJob]);
  const isClosed = closeDate.trim() !== '';
  const isFinanced = financeCompany !== 'None';
  const companyConfig = getCompanyConfig(financeCompany);
  const isCustomCompany = financeCompany === 'Other';
  // Get the current finance rate
  const currentFinanceRate = useMemo(() => {
    if (!isClosed || !isFinanced) return 0;
    if (companyConfig?.noFee) return 0;
    if (isCustomCompany || companyConfig?.customRate) return (parseFloat(customFinanceRate) || 0) / 100;
    if (companyConfig && companyConfig.plans.length > 0) {
      return companyConfig.plans[selectedPlanIndex]?.rate || 0;
    }
    return 0;
  }, [isClosed, isFinanced, isCustomCompany, customFinanceRate, companyConfig, selectedPlanIndex]);
  // Calculate the dollar fee
  const projectTotalNum = isClosed ? parseFloat(projectTotal) || 0 : 0;
  const calculatedFee = projectTotalNum * currentFinanceRate;
  // Reset plan selection when company changes
  useEffect(() => {
    setSelectedPlanIndex(0);
    setCustomFinanceRate('');
    if (!isFinanced) {
      // nothing
    } else {
      setDepositPaid(false);
    }
  }, [financeCompany]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const finalProjectType = projectType === 'Other' ? customProjectType : projectType;
    const finalFinanceCompany = financeCompany === 'Other' ? customFinanceCompany : financeCompany;
    onSubmit({
      editingId: editingJob?.id || null,
      sitDate,
      closeDate,
      isClosed,
      customerName,
      setterName: setterName.trim(),
      // Project details (only relevant if closed)
      projectType: isClosed ? finalProjectType : '',
      projectTotal: projectTotalNum,
      financeCompany: isClosed ? finalFinanceCompany : 'None',
      financeRate: isClosed && isFinanced ? currentFinanceRate : 0,
      financeFee: isClosed && isFinanced ? calculatedFee : 0,
      closerName: isClosed && finalProjectType !== 'General Contracting' && finalProjectType !== 'Hardscape' ? closerName.trim() : '',
      depositPaid: isClosed && isFinanced ? true : isClosed ? depositPaid : false
    });
  };
  const resetForm = () => {
    setSitDate(new Date().toISOString().split('T')[0]);
    setCloseDate('');
    setCustomerName('');
    setSetterName('');
    setProjectType(PROJECT_TYPES[0]);
    setCustomProjectType('');
    setProjectTotal('');
    setFinanceCompany('None');
    setCustomFinanceCompany('');
    setSelectedPlanIndex(0);
    setCustomFinanceRate('');
    setCloserName('');
    setDepositPaid(false);
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-id="element-26">
      <div className={`px-6 py-4 border-b border-slate-100 flex items-center gap-2 ${editingJob ? 'bg-amber-50/50' : 'bg-slate-50/50'}`} data-id="element-27">
        <PlusCircle className={`w-5 h-5 ${editingJob ? 'text-amber-600' : 'text-indigo-600'}`} data-id="element-28" />
        <h2 className="text-lg font-semibold text-slate-800" data-id="element-29">
          {editingJob ? 'Edit Job' : 'New Job Entry'}
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="p-6" data-id="element-30">
        {/* Core Details (Always Required) */}
        <div className="mb-6" data-id="element-31">
          <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4" data-id="element-32">
            Appointment Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-33">
            {/* Sit Date */}
            <div data-id="element-34">
              <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-35">
                Sit Date <span className="text-red-500" data-id="element-36">*</span>
              </label>
              <div className="relative" data-id="element-37">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-38">
                  <Calendar className="w-4 h-4" data-id="element-39" />
                </div>
                <input type="date" required value={sitDate} onChange={e => setSitDate(e.target.value)} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-40" />
              </div>
            </div>

            {/* Customer Name */}
            <div data-id="element-41">
              <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-42">
                Customer Name <span className="text-red-500" data-id="element-43">*</span>
              </label>
              <div className="relative" data-id="element-44">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-45">
                  <User className="w-4 h-4" data-id="element-46" />
                </div>
                <input type="text" required placeholder="John Doe" value={customerName} onChange={e => setCustomerName(e.target.value)} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-47" />
              </div>
            </div>

            {/* Setter */}
            <div data-id="element-48">
              <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-49">
                Setter <span className="text-red-500" data-id="element-50">*</span>
              </label>
              <input type="text" required placeholder="Setter Name" value={setterName} onChange={e => setSetterName(e.target.value)} list="reps-list" className="w-full py-2 px-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-51" />
            </div>

            {/* Close Date (Optional) */}
            <div data-id="element-52">
              <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-53">
                Close Date{' '}
                <span className="text-slate-400 font-normal" data-id="element-54">(Optional)</span>
              </label>
              <div className="relative" data-id="element-55">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-56">
                  <Calendar className="w-4 h-4" data-id="element-57" />
                </div>
                <input type="date" value={closeDate} onChange={e => setCloseDate(e.target.value)} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-58" />
              </div>
              <p className="text-xs text-slate-500 mt-1" data-id="element-59">
                Leave blank if it didn't close
              </p>
            </div>
          </div>
        </div>

        {/* Project Details (Conditional) */}
        <div className={`transition-all duration-300 ${isClosed ? 'opacity-100' : 'opacity-50 grayscale pointer-events-none'}`} data-id="element-60">
          <div className="pt-6 border-t border-slate-100" data-id="element-61">
            <h3 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2" data-id="element-62">
              Project Details
              {!isClosed && <span className="text-xs font-normal text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full normal-case" data-id="element-63">
                  Requires Close Date
                </span>}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-64">
              {/* Project Type */}
              <div data-id="element-65">
                <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-66">
                  Project Type
                </label>
                <div className="relative" data-id="element-67">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-68">
                    <Briefcase className="w-4 h-4" data-id="element-69" />
                  </div>
                  <select value={projectType} onChange={e => setProjectType(e.target.value)} disabled={!isClosed} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all appearance-none bg-white" data-id="element-70">
                    {PROJECT_TYPES.map(type => <option key={type} value={type} data-id="element-71">
                        {type}
                      </option>)}
                  </select>
                </div>
                {isClosed && <p className="text-xs text-slate-500 mt-1.5" data-id="element-72">
                    {projectType === 'Windows' && 'Standard commission % for closer & setter'}
                    {projectType === 'Roofs' && 'Closer capped at $1,000 · Setter capped at $500'}
                    {projectType === 'General Contracting' && 'Setter only — capped at $500 (under $20k) or $1,000 (over $20k)'}
                    {projectType === 'Hardscape' && 'Setter only — capped at $500 (under $20k) or $1,000 (over $20k)'}
                  </p>}
              </div>

              {/* Project Total */}
              <div data-id="element-73">
                <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-74">
                  Project Total
                </label>
                <div className="relative" data-id="element-75">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-76">
                    <DollarSign className="w-4 h-4" data-id="element-77" />
                  </div>
                  <input type="number" required={isClosed} disabled={!isClosed} min="0" step="0.01" placeholder="0.00" value={projectTotal} onChange={e => setProjectTotal(e.target.value)} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-78" />
                </div>
              </div>

              {/* Closer (not needed for General Contracting or Hardscape) */}
              <div className={projectType === 'General Contracting' || projectType === 'Hardscape' ? 'opacity-40 pointer-events-none' : ''} data-id="element-79">
                <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-80">
                  Closer{' '}
                  {(projectType === 'General Contracting' || projectType === 'Hardscape') && <span className="text-xs font-normal text-slate-400" data-id="element-81">
                      (N/A)
                    </span>}
                </label>
                <input type="text" required={isClosed && projectType !== 'General Contracting' && projectType !== 'Hardscape'} disabled={!isClosed || projectType === 'General Contracting' || projectType === 'Hardscape'} placeholder={projectType === 'General Contracting' || projectType === 'Hardscape' ? 'Not applicable' : 'Closer Name'} value={projectType === 'General Contracting' || projectType === 'Hardscape' ? '' : closerName} onChange={e => setCloserName(e.target.value)} list="reps-list" className="w-full py-2 px-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-82" />
              </div>

              {/* Finance Company */}
              <div data-id="element-83">
                <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-84">
                  Finance Company
                </label>
                <div className="relative" data-id="element-85">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-86">
                    <Building className="w-4 h-4" data-id="element-87" />
                  </div>
                  <select value={financeCompany} onChange={e => setFinanceCompany(e.target.value)} disabled={!isClosed} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all appearance-none bg-white" data-id="element-88">
                    {FINANCE_COMPANY_NAMES.map(company => <option key={company} value={company} data-id="element-89">
                        {company}
                      </option>)}
                  </select>
                </div>
                {financeCompany === 'Other' && <input type="text" required={isClosed} disabled={!isClosed} placeholder="Specify finance company" value={customFinanceCompany} onChange={e => setCustomFinanceCompany(e.target.value)} className="w-full mt-2 py-2 px-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-90" />}
              </div>

              {/* Finance Plan / Rate (Conditional) */}
              {isFinanced && <motion.div initial={{
              opacity: 0,
              height: 0
            }} animate={{
              opacity: 1,
              height: 'auto'
            }} data-id="element-91">
                  {companyConfig?.noFee ? <div className="px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-lg" data-id="element-92">
                      <span className="text-sm font-medium text-emerald-700" data-id="element-93">
                        No dealer fee — commissions based on full project total
                      </span>
                    </div> : companyConfig?.plans && companyConfig.plans.length > 0 ? <>
                      <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-94">
                        Finance Plan
                      </label>
                      <select value={selectedPlanIndex} onChange={e => setSelectedPlanIndex(parseInt(e.target.value))} disabled={!isClosed} className="w-full py-2 px-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all appearance-none bg-white" data-id="element-95">
                        {companyConfig.plans.map((plan, idx) => <option key={idx} value={idx} data-id="element-96">
                            {plan.label}
                          </option>)}
                      </select>
                    </> : <>
                      <label className="block mb-2 text-sm font-medium text-slate-700" data-id="element-97">
                        Finance Fee %
                      </label>
                      <div className="relative" data-id="element-98">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-400" data-id="element-99">
                          <Percent className="w-4 h-4" data-id="element-100" />
                        </div>
                        <input type="number" required={isClosed} disabled={!isClosed} min="0" max="100" step="0.01" placeholder="e.g. 15" value={customFinanceRate} onChange={e => setCustomFinanceRate(e.target.value)} className="w-full py-2 pl-10 pr-3 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition-all" data-id="element-101" />
                      </div>
                    </>}

                  {/* Calculated Fee Preview */}
                  {projectTotalNum > 0 && currentFinanceRate > 0 && <div className="mt-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-102">
                      <div className="flex justify-between text-sm" data-id="element-103">
                        <span className="text-amber-700" data-id="element-104">
                          Finance Fee ({(currentFinanceRate * 100).toFixed(2)}%)
                        </span>
                        <span className="font-semibold text-amber-800" data-id="element-105">
                          {formatCurrency(calculatedFee)}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm mt-1" data-id="element-106">
                        <span className="text-amber-700" data-id="element-107">Net to Company</span>
                        <span className="font-semibold text-emerald-700" data-id="element-108">
                          {formatCurrency(projectTotalNum - calculatedFee)}
                        </span>
                      </div>
                    </div>}
                </motion.div>}

              {/* Deposit Paid (Conditional) */}
              {!isFinanced && <div className="flex items-center h-full pt-6" data-id="element-109">
                  <label className="flex items-center gap-3 cursor-pointer group" data-id="element-110">
                    <div className="relative flex items-center" data-id="element-111">
                      <input type="checkbox" checked={depositPaid} onChange={e => setDepositPaid(e.target.checked)} disabled={!isClosed} className="w-5 h-5 border-2 border-slate-300 rounded text-indigo-600 focus:ring-indigo-500 focus:ring-offset-0 transition-all cursor-pointer disabled:opacity-50" data-id="element-112" />
                    </div>
                    <span className="text-sm font-medium text-slate-700 group-hover:text-slate-900 transition-colors" data-id="element-113">
                      Deposit Paid
                    </span>
                  </label>
                </div>}
            </div>
          </div>
        </div>

        {/* Datalist for Reps Autocomplete */}
        <datalist id="reps-list" data-id="element-114">
          {reps.map(rep => <option key={rep.id} value={rep.name} data-id="element-115" />)}
        </datalist>

        <div className="mt-8 flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-116">
          <div className="text-sm" data-id="element-117">
            {setterName && <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-emerald-50 text-emerald-700 font-medium border border-emerald-200" data-id="element-118">
                Sit Pay: {formatCurrency(SIT_PAY_AMOUNT)}
              </span>}
          </div>
          <div className="flex gap-3" data-id="element-119">
            {editingJob ? <button type="button" onClick={() => {
            resetForm();
            onCancelEdit();
          }} className="px-5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-120">
                Cancel
              </button> : <button type="button" onClick={resetForm} className="px-5 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors" data-id="element-121">
                Clear
              </button>}
            <button type="submit" className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors shadow-sm ${editingJob ? 'bg-amber-600 hover:bg-amber-700' : 'bg-indigo-600 hover:bg-indigo-700'}`} data-id="element-122">
              {editingJob ? 'Update Job' : 'Save Job'}
            </button>
          </div>
        </div>
      </form>
    </motion.div>;
}